import React from 'react'
import { observer } from 'mobx-react-lite'
import { Box, Button, Stack, useTheme } from '@mui/material'
import { Icon, Text } from '@mxenabled/mxui'

import { GlobalDataContext } from '../context/GlobalDataProvider'

export const BlobSVG = React.forwardRef<SVGSVGElement, React.SVGProps<SVGSVGElement>>(
  (props, ref) => {
    const theme = useTheme()
    return (
      <svg
        fill="none"
        height="60"
        ref={ref}
        viewBox="0 0 72 60"
        width="72"
        xmlns="http://www.w3.org/2000/svg"
        {...props}
      >
        <path
          d="M28.8288 1.43265C34.4433 4.02036 36.9443 13.1615 44.4378 18.7551C53.8387 25.7725 64.7205 8.05287 70.6592 18.2364C80.9926 35.9558 28.2828 74.5237 25.8072 54.2678C25.1894 49.213 34.1353 42.7165 27.308 38.2971C18.7065 32.7294 9.89746 43.1722 4.34888 37.9733C-8.70438 25.7428 10.0261 -7.2333 28.8288 1.43265Z"
          fill={theme.palette.primary[100] ?? '#DFE8FD'}
        />
      </svg>
    )
  },
)

BlobSVG.displayName = 'BlobSVG'

interface ErrorProps {
  heading?: string
  message?: string
  iconName?: string
  buttonText?: string
  onClick?: () => void
}

const Error = ({
  heading: headingProp,
  message: messageProp,
  iconName = 'error',
  buttonText: buttonTextProp,
  onClick,
}: ErrorProps) => {
  // This is a unique case to handle instance of the error component being used outside the context of the needed provider
  // For example, we still need to show something in the event all network request fail (like copy)
  // This allows us to fall back to hard coded english
  // In a perfect world this will never happen
  // Using React.useContext(GlobalDataContext) directly instead of the useGlobalCopyStore hook
  // returns null (not throwing) when there's no provider, so we can safely use optional chaining (context?.globalStore...)
  // and provide hardcoded fallback strings ('Something went wrong', etc.) when neither props nor context copy are available
  const context = React.useContext(GlobalDataContext)
  const copy = context?.globalStore.copyStore.copy.common

  const heading = headingProp ?? (copy?.error_heading ? `${copy.error_heading}!` : 'Oops!')
  const message = messageProp ?? copy?.error_message ?? 'Something went wrong. Please try again.'
  const buttonText = buttonTextProp ?? copy?.refresh_button ?? 'Refresh'

  const handleClick = () => {
    onClick?.()
  }

  return (
    <Stack alignItems="center" justifyContent="center" style={{ height: '100dvh' }}>
      <Stack
        sx={{
          display: 'flex',
          padding: '48px 16px',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Stack
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              zIndex: 0,
            }}
          >
            <BlobSVG />
          </Box>
          <Icon color="primary" name={iconName} size={48} sx={{ zIndex: 1 }} />
        </Stack>
        <Text bold={true} color="text.primary" mt="24px" variant="h2">
          {heading}
        </Text>
        <Text color="text.secondary" variant="subtitle1">
          {message}
        </Text>
        {onClick && (
          <Button color="primary" onClick={handleClick} sx={{ mt: 48 }} variant="contained">
            {buttonText}
          </Button>
        )}
      </Stack>
    </Stack>
  )
}

export default observer(Error)
